import React, {useState} from "react";
import {addUnit, deleteUnit, submitUnit, updateUnit} from "../api/projects";
import {FACINGS, FURNISHING, PROPERTY_TYPES} from "../constants";
import {validateListing} from "../validation";
import {Field, formatPrice, numberOrNull} from "./Fields";
import PhotoManager from "./PhotoManager";
import DocumentUpload from "./DocumentUpload";
import StatusBadge from "./StatusBadge";

const EMPTY_UNIT = {
    title: "", type: "apartment", bedrooms: "", bhk: "", bathrooms: "", sqft: "", carpetArea: "", builtupArea: "",
    price: "", facing: "", furnishing: "", floorPlanUrl: "", images: [],
};

const unitForm = (u) => {
    const merged = {...u, ...(u.pendingChanges || {})};
    const out = {...EMPTY_UNIT};
    Object.keys(EMPTY_UNIT).forEach((k) => {
        if (merged[k] !== null && merged[k] !== undefined) out[k] = Array.isArray(merged[k]) ? merged[k] : String(merged[k]);
    });
    out.price = u.priceRaw ?? "";
    out.bhk = "";
    return out;
};

const unitPayload = (f) => {
    const text = (v) => (v && String(v).trim()) || null;
    return {
        title: text(f.title), type: f.type, bedrooms: numberOrNull(f.bedrooms), bhk: text(f.bhk),
        bathrooms: numberOrNull(f.bathrooms), sqft: numberOrNull(f.sqft), carpetArea: text(f.carpetArea),
        builtupArea: text(f.builtupArea), price: numberOrNull(f.price), facing: text(f.facing),
        furnishing: text(f.furnishing), floorPlanUrl: text(f.floorPlanUrl), images: f.images,
    };
};

/** Add or edit one unit type. Not a <form>: it sits inside the project's form. */
const UnitPanel = ({project, unit, onDone, onCancel}) => {
    const [form, setForm] = useState(unit ? unitForm(unit) : EMPTY_UNIT);
    const [errors, setErrors] = useState({});
    const [state, setState] = useState({saving: false, error: ""});
    const set = (k) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => ({...f, [k]: value}));
        setErrors((er) => ({...er, [k]: undefined}));
    };

    const save = async () => {
        const found = validateListing(form, {unit: true});
        setErrors(found);
        if (Object.keys(found).length) return;
        setState({saving: true, error: ""});
        try {
            if (unit) await updateUnit(project.id, unit.id, unitPayload(form));
            else await addUnit(project.id, unitPayload(form));
            await onDone();
        } catch (err) {
            setState({saving: false, error: err.message});
        }
    };

    return (
        <div className="brk-unit-panel"
             onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault(); }}>
            <h3 className="brk-unit-panel__title">{unit ? `Edit ${unit.title}` : "Add a unit type"}</h3>
            <div className="brk-grid">
                <Field label="Bedrooms" required error={errors.bedrooms}>
                    <input className="brk-input" type="number" min="0" value={form.bedrooms} onChange={set("bedrooms")}/>
                </Field>
                <Field label="Type">
                    <select className="brk-input" value={form.type} onChange={set("type")}>
                        {PROPERTY_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                </Field>
                <Field label="Price (₹)" required error={errors.price} hint={formatPrice(form.price) || "Starting price for this type"}>
                    <input className="brk-input" type="number" min="0" inputMode="numeric" value={form.price} onChange={set("price")}/>
                </Field>
                <Field label="Bathrooms" error={errors.bathrooms}>
                    <input className="brk-input" type="number" min="0" value={form.bathrooms} onChange={set("bathrooms")}/>
                </Field>
                <Field label="Super built-up (sqft)" error={errors.sqft}>
                    <input className="brk-input" type="number" min="0" value={form.sqft} onChange={set("sqft")}/>
                </Field>
                <Field label="Carpet area"><input className="brk-input" value={form.carpetArea} onChange={set("carpetArea")} placeholder="e.g. 1,050 sqft"/></Field>
                <Field label="Facing">
                    <select className="brk-input" value={form.facing} onChange={set("facing")}>
                        <option value="">Various</option>{FACINGS.map((v) => <option key={v}>{v}</option>)}
                    </select>
                </Field>
                <Field label="Furnishing">
                    <select className="brk-input" value={form.furnishing} onChange={set("furnishing")}>
                        <option value="">—</option>{FURNISHING.map((v) => <option key={v}>{v}</option>)}
                    </select>
                </Field>
                <Field label="Name (optional)" hint="Leave empty for “3 BHK Apartment in …”">
                    <input className="brk-input" value={form.title} onChange={set("title")} placeholder="e.g. 3 BHK Premium"/>
                </Field>
                <Field label="Floor plan" group><DocumentUpload value={form.floorPlanUrl} onChange={set("floorPlanUrl")} label="Upload floor plan"/></Field>
                <Field label="Unit photos (optional)" wide group hint="Show the unit itself; the project's photos are added to every unit.">
                    <PhotoManager value={form.images} onChange={set("images")} max={15}/>
                </Field>
            </div>
            {state.error && <p className="brk-form-error" role="alert">⚠ {state.error}</p>}
            <div className="brk-actions">
                <button type="button" className="brk-btn brk-btn--ghost" onClick={onCancel}>Cancel</button>
                <button type="button" className="brk-btn brk-btn--primary" disabled={state.saving} onClick={save}>
                    {state.saving ? "Saving…" : unit ? "Save unit type" : "Add unit type"}
                </button>
            </div>
        </div>
    );
};

const UnitTypes = ({project, onChanged, error}) => {
    const [editing, setEditing] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [actionError, setActionError] = useState("");
    const projectLive = project.reviewStatus === "APPROVED";

    const run = async (fn) => {
        setActionError("");
        try {
            await fn();
            await onChanged();
        } catch (err) {
            setActionError(err.message);
        } finally {
            setConfirming(null);
        }
    };

    const done = async () => {
        setEditing(null);
        await onChanged();
    };

    return (
        <section className={`brk-section${error ? " brk-units--error" : ""}`}>
            <div className="brk-section__head">
                <div>
                    <h2 className="brk-section__title">Unit types</h2>
                    <p className="brk-section__subtitle">
                        One row per configuration. Each appears in search with the project's photos, amenities and location.
                        {projectLive && " New unit types are reviewed on their own before they go live."}
                    </p>
                </div>
                {editing === null && (
                    <button type="button" className="brk-btn brk-btn--primary brk-btn--sm" onClick={() => setEditing("new")}>
                        + Add unit type
                    </button>
                )}
            </div>
            {error && <p className="brk-field__error" role="alert">{error}</p>}
            {actionError && <div className="brk-notice brk-notice--error" role="alert">{actionError}</div>}
            {editing === "new" && <UnitPanel project={project} onDone={done} onCancel={() => setEditing(null)}/>}

            {project.units.length === 0 && editing !== "new" ? (
                <p className="brk-empty">No unit types yet.</p>
            ) : (
                <ul className="brk-units">
                    {project.units.map((u) => (
                        <li key={u.id} className="brk-unit">
                            {editing === u.id ? (
                                <UnitPanel project={project} unit={u} onDone={done} onCancel={() => setEditing(null)}/>
                            ) : (
                                <div className="brk-unit__row">
                                    <div>
                                        <div className="brk-strong">{u.title}</div>
                                        <div className="brk-hint">
                                            {[u.price, u.sqft && `${u.sqft} sqft`, u.bathrooms && `${u.bathrooms} bath`, u.facing].filter(Boolean).join(" · ")}
                                            {u.soldOut && " · Sold out"}
                                        </div>
                                    </div>
                                    <StatusBadge status={u.reviewStatus} active={projectLive ? u.active : undefined} pendingChanges={u.pendingChanges}/>
                                    {confirming === u.id ? (
                                        <span className="brk-row-actions">
                                            <span className="brk-hint">Delete this unit type?</span>
                                            <button type="button" className="brk-link brk-link--danger" onClick={() => run(() => deleteUnit(project.id, u.id))}>Yes</button>
                                            <button type="button" className="brk-link" onClick={() => setConfirming(null)}>No</button>
                                        </span>
                                    ) : (
                                        <span className="brk-row-actions">
                                            {u.reviewStatus !== "REJECTED" && <button type="button" className="brk-link" onClick={() => setEditing(u.id)}>Edit</button>}
                                            {projectLive && ["DRAFT", "CHANGES_REQUESTED"].includes(u.reviewStatus) && (
                                                <button type="button" className="brk-link" onClick={() => run(() => submitUnit(project.id, u.id))}>Submit</button>
                                            )}
                                            {u.reviewStatus === "APPROVED" && (
                                                <button type="button" className="brk-link"
                                                        onClick={() => run(() => updateUnit(project.id, u.id, {soldOut: !u.soldOut}))}>
                                                    {u.soldOut ? "Mark available" : "Mark sold out"}
                                                </button>
                                            )}
                                            <button type="button" className="brk-link brk-link--danger" onClick={() => setConfirming(u.id)}>Delete</button>
                                        </span>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default UnitTypes;
